/**
 * Brand Wizard — Create a new Brand Brain
 * 3-step overlay: Name → Profile (with URL scan) → Done
 *
 * Shares VOICE_OPTIONS + matchVoiceOption + field labels with the Setup
 * Wizard via app/src/data/brandProfileSchema.js. Visual styling stays
 * separate (this wizard uses the modal-style `onboard-*` classes; setup
 * uses the onboarding-pane `h-*` classes).
 */
import { client } from '../api/client.js';
import { BRAND_VOICE_OPTIONS, matchVoiceOption } from '../data/brandProfileSchema.js';

const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

export function showBrandWizard(opts = {}) {
    const { onComplete } = opts;
    let step = 1;
    let brandName = '';
    let mission = '';
    let audience = '';
    let voice = 'Professional';
    let offers = '';
    let cta = '';
    let keywords = '';
    let avoid_words = '';
    let customVoice = '';
    let scanUrl = '';
    let scanning = false;
    let scanError = '';
    let scanSuggestions = null;
    let scanWarning = '';
    let scanOpen = false;
    let detailsOpen = false; // "More details" disclosure survives re-renders
    let saving = false;
    let error = '';
    // Set once createBrand succeeds. Two jobs: (1) a retry after a partial
    // failure (e.g. switchBrand threw) must NOT create a duplicate brand;
    // (2) any dismissal after creation must fire onComplete so the caller
    // refreshes — the active brand already changed under the current page.
    let createdBrandId = null;

    const overlay = document.createElement('div');
    overlay.className = 'onboard-overlay brand-wizard-overlay';
    overlay.setAttribute('data-testid', 'brand-wizard-overlay');

    const closeBtn = `<button class="brand-wizard-close" data-action="cancel" title="Close" aria-label="Close"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`;

    function renderStep() {
        if (step === 1) {
            overlay.innerHTML = `
                <div class="onboard-card brand-wizard-card" data-testid="brand-wizard-card">
                    ${closeBtn}
                    <div class="onboard-step">STEP 1 OF 3</div>
                    <div class="onboard-title">Name your new brand</div>
                    <div class="onboard-desc">Each brand keeps its own voice, audience, and content — nothing is shared between them.</div>
                    <div class="onboard-field">
                        <label class="onboard-label">Brand Name</label>
                        <input class="onboard-input" data-field="brandName" placeholder="e.g. Acme Corp, My Side Hustle" value="${_esc(brandName)}" autofocus>
                    </div>
                    ${error ? `<div class="onboard-scan__error">${_esc(error)}</div>` : ''}
                    <div class="onboard-actions">
                        <button class="onboard-primary" data-action="next" ${!brandName.trim() ? 'disabled' : ''}>Continue</button>
                        <button class="onboard-ghost" data-action="cancel">Cancel</button>
                    </div>
                </div>
            `;
        } else if (step === 2) {
            // Voice presets from the shared schema so this and the Setup
            // Wizard never drift on what counts as a recognised tone.
            const voicePresets = BRAND_VOICE_OPTIONS;
            const isCustomVoice = voice === 'Other' || (!voicePresets.includes(voice) && voice);
            const selectedVoice = isCustomVoice ? 'Other' : voice;
            overlay.innerHTML = `
                <div class="onboard-card brand-wizard-card" data-testid="brand-wizard-card">
                    ${closeBtn}
                    <div class="onboard-step">STEP 2 OF 3</div>
                    <div class="onboard-title">Tell us about ${_esc(brandName)}</div>
                    <div class="onboard-desc">A few details so posts for this brand sound like you. Everything is editable later.</div>

                    <div class="onboard-field">
                        <label class="onboard-label">Mission / What this brand does</label>
                        <textarea class="onboard-input" data-field="mission" rows="2" placeholder="e.g. We help small businesses grow...">${_esc(mission)}</textarea>
                    </div>
                    <div class="onboard-field">
                        <label class="onboard-label">Target Audience</label>
                        <textarea class="onboard-input" data-field="audience" rows="2" placeholder="e.g. Small business owners, marketers...">${_esc(audience)}</textarea>
                    </div>
                    <div class="onboard-field">
                        <label class="onboard-label">Voice / Tone</label>
                        <select class="onboard-select" data-field="voice">
                            ${voicePresets.map(v =>
                                `<option value="${v}"${v === selectedVoice ? ' selected' : ''}>${v}</option>`
                            ).join('')}
                            <option value="Other"${selectedVoice === 'Other' ? ' selected' : ''}>Other (custom)</option>
                        </select>
                        ${isCustomVoice ? `
                        <input class="onboard-input" data-field="customVoice" placeholder="e.g. Sarcastic, Gen-Z, Corporate-but-fun..."
                               value="${_esc(customVoice)}" style="margin-top:8px;">
                        ` : ''}
                    </div>

                    <details class="onboard-more" ${detailsOpen || offers || cta || keywords || avoid_words ? 'open' : ''}>
                        <summary style="cursor:pointer;font-size:12px;opacity:0.8;margin:4px 0 8px;" data-action="toggle-details">More details (optional) — offers, call-to-action, keywords</summary>
                        <div class="onboard-field">
                            <label class="onboard-label">What you offer / sell</label>
                            <input class="onboard-input" data-field="offers" placeholder="e.g. Coffee subscriptions, brewing classes" value="${_esc(offers)}">
                        </div>
                        <div class="onboard-field">
                            <label class="onboard-label">Call to action — what should posts ask readers to do?</label>
                            <input class="onboard-input" data-field="cta" placeholder="e.g. Shop the roast of the month" value="${_esc(cta)}">
                        </div>
                        <div class="onboard-field">
                            <label class="onboard-label">Keywords to use</label>
                            <input class="onboard-input" data-field="keywords" placeholder="e.g. specialty coffee, home brewing" value="${_esc(keywords)}">
                        </div>
                        <div class="onboard-field">
                            <label class="onboard-label">Words to avoid</label>
                            <input class="onboard-input" data-field="avoid_words" placeholder="e.g. cheap, discount" value="${_esc(avoid_words)}">
                        </div>
                    </details>

                    <div class="onboard-scan ${scanOpen ? 'onboard-scan--open' : ''}">
                        <div class="onboard-scan__header" data-action="toggle-scan" style="cursor:pointer;user-select:none;">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                            <span>Auto-fill from website — paste your site and we'll suggest the fields above</span>
                            <span style="margin-left:auto;font-size:11px;opacity:0.6;">(optional)</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left:4px;transition:transform 0.2s;${scanOpen ? 'transform:rotate(180deg);' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
                        </div>
                        ${scanOpen ? `
                        <div class="onboard-scan__row" style="margin-top:10px;">
                            <input class="onboard-input onboard-scan__input" data-field="scanUrl"
                                   placeholder="https://yourwebsite.com" value="${_esc(scanUrl)}" type="url">
                            <button class="onboard-scan__btn" data-action="scan" ${scanning ? 'disabled' : ''}>
                                ${scanning ? '<span class="onboard-scan__spinner"></span> Scanning...' : 'Scan'}
                            </button>
                        </div>
                        ${scanError ? `<div class="onboard-scan__error">${_esc(scanError)}</div>` : ''}
                        ${scanSuggestions ? `
                        <div class="onboard-scan__suggestions">
                            ${scanWarning ? `<div class="onboard-scan__error" style="margin-bottom:6px;">${_esc(scanWarning)}</div>` : ''}
                            <div class="onboard-scan__sug-label">${scanWarning ? 'Generic starter suggestions (please rewrite for your brand):' : "Here's what we found on your site — edit anything that's off:"}</div>
                            ${scanSuggestions.mission ? `<div class="onboard-scan__sug-item"><strong>Mission:</strong> ${_esc(scanSuggestions.mission)}</div>` : ''}
                            ${scanSuggestions.audience ? `<div class="onboard-scan__sug-item"><strong>Audience:</strong> ${_esc(scanSuggestions.audience)}</div>` : ''}
                            ${scanSuggestions.voice ? `<div class="onboard-scan__sug-item"><strong>Voice:</strong> ${_esc(scanSuggestions.voice)}</div>` : ''}
                            ${scanSuggestions.offers ? `<div class="onboard-scan__sug-item"><strong>Offers:</strong> ${_esc(scanSuggestions.offers)}</div>` : ''}
                            ${scanSuggestions.cta ? `<div class="onboard-scan__sug-item"><strong>CTA:</strong> ${_esc(scanSuggestions.cta)}</div>` : ''}
                            ${scanSuggestions.keywords ? `<div class="onboard-scan__sug-item"><strong>Keywords:</strong> ${_esc(scanSuggestions.keywords)}</div>` : ''}
                            ${scanSuggestions.avoid_words ? `<div class="onboard-scan__sug-item"><strong>Avoid:</strong> ${_esc(scanSuggestions.avoid_words)}</div>` : ''}
                            <button class="onboard-scan__apply" data-action="apply-scan">Apply Suggestions</button>
                        </div>` : ''}
                        ` : ''}
                    </div>

                    ${error ? `<div class="onboard-scan__error">${_esc(error)}</div>` : ''}
                    <div class="onboard-actions">
                        <button class="onboard-primary" data-action="save" ${saving ? 'disabled' : ''}>
                            ${saving ? 'Creating...' : 'Create brand'}
                        </button>
                        <button class="onboard-ghost" data-action="back">Back</button>
                    </div>
                </div>
            `;
        } else if (step === 3) {
            overlay.innerHTML = `
                <div class="onboard-card brand-wizard-card" data-testid="brand-wizard-card">
                    ${closeBtn}
                    <div class="onboard-title" style="margin-top:8px;">${_esc(brandName)} is ready!</div>
                    <div class="onboard-desc">
                        Your new brand is set up and active. You can switch between brands
                        using the dropdown in the sidebar, or manage them in Settings.
                    </div>
                    <div class="onboard-checklist">
                        <div class="onboard-check-item done">Brand created</div>
                        <div class="onboard-check-item done">Brand details saved</div>
                        <div class="onboard-check-item done">Switched to ${_esc(brandName)}</div>
                    </div>
                    <div class="onboard-actions">
                        <button class="onboard-primary" data-action="go-brain">Start creating content</button>
                        <button class="onboard-ghost" data-action="close">Close</button>
                    </div>
                </div>
            `;
        }
    }

    // Single dismissal path for X / Cancel / Close / Escape / click-outside.
    // Once the brand exists, the active brand has already switched under the
    // current page, so EVERY dismissal must fire onComplete — previously only
    // the "Close" button did, leaving the sidebar/brand list stale after an
    // Escape or X. Also always detaches the Escape listener (it used to leak
    // on every non-Escape close).
    function dismiss() {
        document.removeEventListener('keydown', onEscape);
        overlay.remove();
        if (createdBrandId && onComplete) onComplete();
    }

    overlay.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;

        if (action === 'cancel' || action === 'close') {
            dismiss();
            return;
        }

        if (action === 'next') {
            if (!brandName.trim()) { error = 'Please enter a brand name'; renderStep(); return; }
            error = '';
            step = 2;
            renderStep();
            return;
        }

        if (action === 'back') {
            step = Math.max(1, step - 1);
            error = '';
            renderStep();
            return;
        }

        if (action === 'toggle-scan') {
            scanOpen = !scanOpen;
            renderStep();
            return;
        }

        // The native <details> toggle handles the UI; just mirror the state
        // so a later re-render (voice change, scan) doesn't collapse it.
        if (action === 'toggle-details') {
            detailsOpen = !detailsOpen;
            return;
        }

        if (action === 'scan') {
            scanUrl = overlay.querySelector('[data-field="scanUrl"]')?.value || '';
            if (!scanUrl.trim()) { scanError = 'Please enter a URL'; renderStep(); return; }
            if (!/^https?:\/\//i.test(scanUrl)) scanUrl = 'https://' + scanUrl;
            scanning = true;
            scanError = '';
            scanSuggestions = null;
            renderStep();
            try {
                const result = await client.suggestProfile(scanUrl);
                if (result.suggestions) scanSuggestions = result.suggestions;
                // The server returns generic stub suggestions plus a warning
                // when no AI key is configured (or the scan failed). Surface
                // it — otherwise boilerplate gets presented as if it was
                // actually read from the user's website.
                scanWarning = result.warning || '';
            } catch (e) {
                scanError = e.message || 'Failed to scan website';
            }
            scanning = false;
            renderStep();
            return;
        }

        if (action === 'apply-scan') {
            if (scanSuggestions) {
                if (scanSuggestions.mission) mission = scanSuggestions.mission;
                if (scanSuggestions.audience) audience = scanSuggestions.audience;
                if (scanSuggestions.voice) {
                    // Route the server's voice value through matchVoiceOption
                    // so a result like "Provocative" (not in voicePresets) lands
                    // in the custom-voice text field instead of being silently
                    // dropped when isCustomVoice resets the select to "Other".
                    const matched = matchVoiceOption(scanSuggestions.voice, BRAND_VOICE_OPTIONS);
                    if (matched.option) {
                        voice = matched.option;
                        customVoice = '';
                    } else {
                        voice = 'Other';
                        customVoice = matched.custom || '';
                    }
                }
                if (scanSuggestions.offers) offers = scanSuggestions.offers;
                if (scanSuggestions.cta) cta = scanSuggestions.cta;
                if (scanSuggestions.keywords) keywords = scanSuggestions.keywords;
                if (scanSuggestions.avoid_words) avoid_words = scanSuggestions.avoid_words;
                scanSuggestions = null;
            }
            renderStep();
            return;
        }

        if (action === 'save') {
            const card = overlay.querySelector('.brand-wizard-card');
            mission = card.querySelector('[data-field="mission"]')?.value || mission;
            audience = card.querySelector('[data-field="audience"]')?.value || audience;
            offers = card.querySelector('[data-field="offers"]')?.value ?? offers;
            cta = card.querySelector('[data-field="cta"]')?.value ?? cta;
            keywords = card.querySelector('[data-field="keywords"]')?.value ?? keywords;
            avoid_words = card.querySelector('[data-field="avoid_words"]')?.value ?? avoid_words;
            const selectedVoice = card.querySelector('[data-field="voice"]')?.value || voice;
            if (selectedVoice === 'Other') {
                customVoice = card.querySelector('[data-field="customVoice"]')?.value || customVoice;
                voice = customVoice.trim() || 'Professional';
            } else {
                voice = selectedVoice;
            }

            saving = true;
            error = '';
            renderStep();

            try {
                // Idempotent on retry: if a previous attempt created the brand
                // but failed later (e.g. switchBrand errored), don't create a
                // duplicate — resume from the existing id.
                if (!createdBrandId) {
                    const result = await client.createBrand(brandName.trim());
                    if (!result.ok && result.error) throw new Error(result.error);
                    createdBrandId = result.id;
                }
                const newBrandId = createdBrandId;

                await client.switchBrand(newBrandId);
                localStorage.setItem('heist_active_brand', newBrandId);

                if (mission || audience || voice || offers || cta || keywords || avoid_words) {
                    try {
                        await client.updateProfile({
                            mission, audience, voice,
                            offers_summary: offers, cta, keywords, avoid_words
                        });
                    } catch (e) {
                        console.warn('[BRAND_WIZARD] Profile save error:', e.message);
                    }
                }

                saving = false;
                step = 3;
                renderStep();
            } catch (e) {
                saving = false;
                error = e.message || 'Failed to create brand';
                renderStep();
            }
            return;
        }

        if (action === 'go-brain') {
            document.removeEventListener('keydown', onEscape);
            overlay.remove();
            window.location.reload();
            return;
        }
    });

    overlay.addEventListener('input', (e) => {
        const field = e.target.dataset?.field;
        if (field === 'brandName') {
            brandName = e.target.value;
            const nextBtn = overlay.querySelector('[data-action="next"]');
            if (nextBtn) nextBtn.disabled = !brandName.trim();
        }
        if (field === 'mission') mission = e.target.value;
        if (field === 'audience') audience = e.target.value;
        if (field === 'scanUrl') scanUrl = e.target.value;
        if (field === 'customVoice') customVoice = e.target.value;
        if (field === 'offers') offers = e.target.value;
        if (field === 'cta') cta = e.target.value;
        if (field === 'keywords') keywords = e.target.value;
        if (field === 'avoid_words') avoid_words = e.target.value;
    });
    overlay.addEventListener('change', (e) => {
        const field = e.target.dataset?.field;
        if (field === 'voice') {
            voice = e.target.value;
            if (voice === 'Other') {
                renderStep();
                const ci = overlay.querySelector('[data-field="customVoice"]');
                if (ci) ci.focus();
                return;
            }
            renderStep();
        }
    });

    // Close on Escape key (routes through dismiss so onComplete fires when
    // a brand was already created, and the listener always detaches).
    function onEscape(e) {
        if (e.key === 'Escape') dismiss();
    }
    document.addEventListener('keydown', onEscape);

    // Close on click outside the card
    overlay.addEventListener('mousedown', (e) => {
        if (e.target === overlay) dismiss();
    });

    renderStep();
    document.body.appendChild(overlay);
}
